import { MonetaryAmount, Shimmer } from '@components'
import { getInvoicingPageUrl } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { IDocument, ImportDataSource } from '@query'
import { IInvoiceRow } from '@query/invoices'
import { useInvoice, usePurchaseInvoice } from '@root/query/hooks/invoices'
import {
  getInvoiceDeliveryIcon,
  PurchaseInvoiceListIcon,
  SalesInvoiceListIcon
} from '@pages/InvoicingPage/invoiceIcons'
import { getInvoiceGrossTotal, groupInvoiceVatByRate } from '@pages/InvoicingPage/invoiceAmounts'
import {
  InvoiceStatusBadge,
  PurchaseInvoiceStatusBadge
} from '@pages/InvoicingPage/InvoiceStatusBadge'
import {
  RelationContent,
  RelationGroupFooter,
  RelationIcon,
  RelationListWrapper,
  RelationRowPrimary,
  RelationRowSecondary
} from '@pages/DocumentPage/DocumentForm/RelationMapper.styled'
import { formatDate } from '@utils'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowRight, FaChevronDown } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import {
  canShowSourceInvoiceWidget,
  isAccrualInvoiceImportSource,
  parseImportInvoiceId
} from './invoiceImportUtils'

const MAX_VISIBLE_ROWS = 5
const themeMode = 'light' as const

interface Props {
  document: IDocument
  fetchState: SourceInvoiceFetchState
}

export function useSourceInvoiceFetch(document: IDocument) {
  const { slug } = useBusinessContext()
  const hasInvoicingView = usePermission(PermissionSet.INVOICING_VIEW)

  const importData = document.import_data
  const dataSource = importData?.data_source
  const invoiceId = parseImportInvoiceId(importData?.identifier)
  const isSales = dataSource === ImportDataSource.SALES_INVOICE
  const isPurchase = dataSource === ImportDataSource.PURCHASE_INVOICE

  const fetchEnabled = Boolean(
    hasInvoicingView && invoiceId && isAccrualInvoiceImportSource(dataSource)
  )

  const salesQuery = useInvoice(
    { invoiceId: isSales ? invoiceId ?? undefined : undefined },
    { enabled: fetchEnabled && isSales }
  )

  const purchaseQuery = usePurchaseInvoice(
    { purchaseInvoiceId: isPurchase ? invoiceId ?? undefined : undefined },
    { enabled: fetchEnabled && isPurchase }
  )

  const invoiceQuery = isSales ? salesQuery : purchaseQuery
  const salesInvoice = isSales ? salesQuery.data : undefined
  const purchaseInvoice = isPurchase ? purchaseQuery.data : undefined

  const invoiceLoaded = invoiceQuery.isSuccess && Boolean(invoiceQuery.data)
  const invoiceLoading = fetchEnabled && invoiceQuery.isFetching && !invoiceLoaded

  const canAttempt = canShowSourceInvoiceWidget(document, hasInvoicingView)
  const showSection = canAttempt && (invoiceLoading || invoiceLoaded)

  const invoicingUrl =
    invoiceId != null && invoiceLoaded
      ? getInvoicingPageUrl(slug, {
          id: invoiceId,
          type: isSales ? 'sales' : 'purchase'
        })
      : null

  return {
    showSection,
    invoiceLoaded,
    invoiceLoading,
    isSales,
    isPurchase,
    salesInvoice,
    purchaseInvoice,
    invoiceId,
    invoicingUrl
  }
}

export type SourceInvoiceFetchState = ReturnType<typeof useSourceInvoiceFetch>

export const SourceInvoiceDocumentSection: React.FC<Props> = ({ document, fetchState }) => {
  const [t] = useTranslation()
  const dataSource = document.import_data?.data_source

  if (!fetchState.showSection) {
    return null
  }

  return (
    <SectionDetailRow>
      <SectionHeader>
        {t(
          dataSource === ImportDataSource.PURCHASE_INVOICE
            ? 'document.sourceInvoice.titlePurchase'
            : 'document.sourceInvoice.title'
        )}
      </SectionHeader>
      <SourceInvoiceWidgetBody document={document} fetchState={fetchState} />
    </SectionDetailRow>
  )
}

const SourceInvoiceWidgetBody: React.FC<{
  document: IDocument
  fetchState: SourceInvoiceFetchState
}> = ({ document, fetchState }) => {
  const [t] = useTranslation()
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)

  const {
    invoiceLoaded,
    invoiceLoading,
    isSales,
    isPurchase,
    salesInvoice,
    purchaseInvoice,
    invoiceId,
    invoicingUrl
  } = fetchState

  const canExpand = invoiceLoaded
  const showInvoicingLink = invoiceLoaded && invoiceId != null

  const contactName =
    document.contact_name ??
    salesInvoice?.receiver_info?.name ??
    purchaseInvoice?.sender_name ??
    null

  const displayNumber = salesInvoice?.invoice_number ?? purchaseInvoice?.invoice_number ?? '—'

  const displayDate = salesInvoice?.invoicing_date ?? purchaseInvoice?.invoicing_date ?? null

  const displayAmount =
    salesInvoice != null
      ? getInvoiceGrossTotal(salesInvoice.total_amount, salesInvoice.total_vat_amount)
      : purchaseInvoice?.amount ?? null

  const dueDate = salesInvoice?.due_date ?? purchaseInvoice?.due_date ?? null

  const reference = salesInvoice?.reference ?? purchaseInvoice?.reference ?? null

  const rowTitle = t('document.sourceInvoice.rowTitle', {
    number: displayNumber,
    date: displayDate ? formatDate(displayDate) : '—'
  })

  const vatLines = useMemo(
    () => groupInvoiceVatByRate(salesInvoice?.rows ?? []),
    [salesInvoice?.rows]
  )

  const salesDeliveryMethod = salesInvoice?.delivery_method
  const salesIsSent = Boolean(salesInvoice?.last_delivery_at && salesDeliveryMethod)
  const SentDeliveryIcon = getInvoiceDeliveryIcon(true)
  const NotSentDeliveryIcon = getInvoiceDeliveryIcon(false)

  const SourceInvoiceIcon = isSales ? SalesInvoiceListIcon : PurchaseInvoiceListIcon
  const sourceInvoiceIconColor = isSales ? theme.colors.primary : theme.colors.warning

  const visibleRows = salesInvoice?.rows?.slice(0, MAX_VISIBLE_ROWS) ?? []
  const hiddenRowCount = Math.max(0, (salesInvoice?.rows?.length ?? 0) - MAX_VISIBLE_ROWS)

  const salesGrossTotal =
    salesInvoice != null
      ? getInvoiceGrossTotal(salesInvoice.total_amount, salesInvoice.total_vat_amount)
      : null

  const toggleExpanded = () => {
    if (!canExpand) {
      return
    }
    setIsOpen(value => !value)
  }

  return (
    <RelationListWrapper $themeMode={themeMode}>
      <ToggleRow
        type="button"
        disabled={!canExpand}
        aria-expanded={isOpen}
        aria-label={
          isOpen ? t('document.sourceInvoice.collapse') : t('document.sourceInvoice.expand')
        }
        onClick={toggleExpanded}
      >
        <RelationIcon $color={sourceInvoiceIconColor}>
          <SourceInvoiceIcon />
        </RelationIcon>
        <RelationContent>
          {invoiceLoading ? (
            <>
              <Shimmer height="0.875rem" width="70%" backgroundColor={theme.colors.neutralWhite} />
              <Shimmer height="0.75rem" width="60%" backgroundColor={theme.colors.neutralWhite} />
            </>
          ) : (
            <>
              <RelationRowPrimary $themeMode={themeMode}>{rowTitle}</RelationRowPrimary>
              <RelationRowSecondary>
                {displayAmount != null ? (
                  <>
                    <MonetaryAmount value={displayAmount} />
                    {contactName ? <span>·</span> : null}
                  </>
                ) : null}
                {contactName ? <span>{contactName}</span> : null}
              </RelationRowSecondary>
            </>
          )}
        </RelationContent>
        <StatusColumn>
          {salesInvoice ? (
            <InvoiceStatusBadge status={salesInvoice.status} size="md" />
          ) : purchaseInvoice ? (
            <PurchaseInvoiceStatusBadge isPaid={purchaseInvoice.is_paid} size="md" />
          ) : null}
          {canExpand ? (
            <ChevronHitArea animate={{ rotateZ: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <FaChevronDown color={theme.colors.iconGray} />
            </ChevronHitArea>
          ) : null}
        </StatusColumn>
      </ToggleRow>

      <AnimatePresence initial={false}>
        {isOpen && canExpand ? (
          <ExpandedPanel
            key="source-invoice-expanded"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {isSales && salesInvoice ? (
              <>
                <FactsGrid>
                  <FactCell>
                    <FactLabel>{t('document.sourceInvoice.dueDate')}</FactLabel>
                    <FactValue>{dueDate ? formatDate(dueDate) : '—'}</FactValue>
                  </FactCell>
                  <FactCell>
                    <FactLabel>{t('document.sourceInvoice.reference')}</FactLabel>
                    <FactValue>{reference ?? '—'}</FactValue>
                  </FactCell>
                  <FactCell>
                    <FactLabel>{t('document.sourceInvoice.deliveryMethod')}</FactLabel>
                    {salesIsSent && salesDeliveryMethod ? (
                      <DeliveryValue>
                        <SentDeliveryIcon color={theme.colors.primary} />
                        <span>{t(`invoicing.deliveryMethod.${salesDeliveryMethod}`)}</span>
                      </DeliveryValue>
                    ) : (
                      <DeliveryValue $muted>
                        <NotSentDeliveryIcon color={theme.colors.iconGray} />
                        <span>{t('invoicing.status.notSent')}</span>
                      </DeliveryValue>
                    )}
                  </FactCell>
                </FactsGrid>
                <RowsBlock>
                  {visibleRows.map(row => (
                    <InvoiceRowLine key={row.id} row={row} />
                  ))}
                  {hiddenRowCount > 0 && invoicingUrl ? (
                    <MoreRowsLink to={invoicingUrl}>
                      {t('document.sourceInvoice.moreRows', { count: hiddenRowCount })}
                    </MoreRowsLink>
                  ) : null}
                </RowsBlock>
                <TotalsBlock>
                  <TotalLine>
                    <span>{t('document.sourceInvoice.subtotal')}</span>
                    <MonetaryAmount value={salesInvoice.total_amount} />
                  </TotalLine>
                  {vatLines.length > 0 ? (
                    vatLines.map(([rate, amount]) => (
                      <TotalLine key={rate} $muted>
                        <span>{t('document.sourceInvoice.vat', { rate })}</span>
                        <MonetaryAmount value={amount} />
                      </TotalLine>
                    ))
                  ) : salesInvoice.total_vat_amount ? (
                    <TotalLine $muted>
                      <span>{t('document.sourceInvoice.vat', { rate: '—' })}</span>
                      <MonetaryAmount value={salesInvoice.total_vat_amount} />
                    </TotalLine>
                  ) : null}
                  {salesGrossTotal != null ? (
                    <TotalLine $strong>
                      <span>{t('document.sourceInvoice.total')}</span>
                      <MonetaryAmount value={salesGrossTotal} />
                    </TotalLine>
                  ) : null}
                </TotalsBlock>
              </>
            ) : null}
            {isPurchase && purchaseInvoice ? (
              <>
                <FactsGrid $columns={2}>
                  <FactCell>
                    <FactLabel>{t('document.sourceInvoice.dueDate')}</FactLabel>
                    <FactValue>{dueDate ? formatDate(dueDate) : '—'}</FactValue>
                  </FactCell>
                  <FactCell>
                    <FactLabel>{t('document.sourceInvoice.reference')}</FactLabel>
                    <FactValue>{reference ?? '—'}</FactValue>
                  </FactCell>
                </FactsGrid>
                <TotalsBlock>
                  <TotalLine $strong>
                    <span>{t('document.sourceInvoice.total')}</span>
                    <MonetaryAmount value={purchaseInvoice.amount} />
                  </TotalLine>
                </TotalsBlock>
              </>
            ) : null}
          </ExpandedPanel>
        ) : null}
      </AnimatePresence>

      <RelationGroupFooter $themeMode={themeMode}>
        <FooterLeft>
          {!isOpen && dueDate && reference ? (
            t('document.sourceInvoice.dueAndReference', {
              date: formatDate(dueDate),
              reference
            })
          ) : !isOpen && dueDate ? (
            <>
              {t('document.sourceInvoice.dueDate')} {formatDate(dueDate)}
            </>
          ) : null}
        </FooterLeft>
        {showInvoicingLink && invoicingUrl ? (
          <InvoicingLink to={invoicingUrl}>
            {t('document.sourceInvoice.openInInvoicing')}
            <FaArrowRight />
          </InvoicingLink>
        ) : null}
      </RelationGroupFooter>
    </RelationListWrapper>
  )
}

const InvoiceRowLine: React.FC<{ row: IInvoiceRow }> = ({ row }) => (
  <RowLine>
    <RowName>{row.name}</RowName>
    <RowQuantity>
      {row.product_count} {row.unit} × <MonetaryAmount value={row.amount} />
    </RowQuantity>
    <RowTotal>
      <MonetaryAmount value={row.total_amount} />
    </RowTotal>
  </RowLine>
)

const SectionDetailRow = styled.div`
  flex: 1;
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;
`

const SectionHeader = styled.p`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
  align-self: flex-start;
`

const ToggleRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  width: 100%;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.sm}rem;
  border: none;
  font: inherit;
  text-align: left;
  color: inherit;
  background: transparent;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  &:disabled {
    cursor: default;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`

const StatusColumn = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-shrink: 0;
`

const ChevronHitArea = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
`

const ExpandedPanel = styled(motion.div)`
  overflow: hidden;
`

const FactsGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  background: ${({ theme }) => theme.colors.defaultHover};

  @media (max-width: 24rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const FactCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
`

const FactLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const FactValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DeliveryValue = styled(FactValue)<{ $muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-weight: 600;
  color: ${({ theme, $muted }) => ($muted ? theme.colors.iconGray : theme.colors.neutralBlack)};
`

const RowsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
`

const RowLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const RowName = styled.span`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const RowQuantity = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const RowTotal = styled.span`
  width: 5.5rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`

const MoreRowsLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const TotalsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-variant-numeric: tabular-nums;
`

const TotalLine = styled.div<{ $muted?: boolean; $strong?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  color: ${({ theme, $muted, $strong }) =>
    $strong ? theme.colors.neutralBlack : $muted ? theme.colors.metalGray : theme.colors.metalGray};
  font-weight: ${({ $strong }) => ($strong ? 700 : 400)};
`

const FooterLeft = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

const InvoicingLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;
  flex-shrink: 0;

  svg {
    width: 0.6875rem;
    height: 0.6875rem;
  }

  &:hover {
    text-decoration: none;
  }
`
